import React, { useEffect, useState } from "react";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { Skeleton } from "primereact/skeleton";
import z from "zod";
import { useParams } from "react-router-dom";
import { InputText } from "primereact/inputtext";
import { Tooltip } from "primereact/tooltip";

import { useSelector } from "react-redux";
import useUserAccess from "../useUserAccess";
import apiService from "../../../services/service";
import API_ROUTES from "../../../api-routes";
import Notification from "../../../components/entities/notification/Notification";

const UserInfo = () => {
  const { id } = useParams();
  const { states, handleAssignLsp } = useUserAccess(id);
  const { isAdmin } = useSelector((state) => state.login);
  const [role, setRole] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [message, setMessage] = useState("");

  const handleRoleChange = async (e) => {
    setRole("");
    try {
      await apiService(API_ROUTES.UPDATE_USER, "PUT", {
        body: {
          role: e.value,
        },
        replace: { id },
        schema: z.object({
          id: z.string().uuid(),
          role: z.enum([
            "CUSTOMER",
            "LSP_USER",
            "GXM_ADMIN",
            "GXM_USER",
            "GXM_MANAGER",
            "LSP_MANAGER",
            "CUSTOMER_MANAGER",
          ]),
        }),
      });
      setRole(e.value);
      setSuccessMessage("User role updated successfully");
    } catch (error) {
      console.log(error);
      setMessage("Something went wrong, please try again later");
      setRole(states?.workosuser?.role);
    }
  };

  useEffect(() => {
    if (states?.workosuser?.role) setRole(states?.workosuser?.role);
  }, [states?.workosuser]);

  return (
    <>
      <Card className="gen-card h-full">
        <div className="flex justify-content-between formgrid grid grid-nogutter w-full">
          <div>
            <p className="profile-heading">User Information</p>
            <p className="profile-para">
              Find all the relevant information related to your profile
            </p>
          </div>
          <Notification
            message={message}
            successMessage={successMessage}
            setMessage={setMessage}
            setSuccessMessage={setSuccessMessage}
          />
          <div className="formgrid grid mt-2 w-full">
            <div className="field col-12 md:col-6 mb-0">
              <label className="input-label">
                Given Name(s) *{" "}
                <i className="pi pi-info-circle" id="given-name-info"></i>
                <Tooltip
                  target="#given-name-info"
                  content="This is usually the person's first name or the name they usually go by."
                  position="right"
                  className="custom-tooltip tooltip-custom-format"
                />
              </label>

              {states?.loadingData ? (
                <Skeleton
                  data-testid="skeleton-loader"
                  className="p-4"
                ></Skeleton>
              ) : (
                <InputText
                  value={states?.workosuser?.first_name}
                  placeholder="Given Name"
                  name="firstName"
                  type="text"
                  disabled
                  className="appearance-none outline-none w-full disabled-input"
                />
              )}
            </div>

            <div className="field col-12 md:col-6 mb-0">
              <label className="input-label">
                Other Name(s)
                <i
                  className="ml-2 pi pi-info-circle info-icon"
                  id="other-name-info"
                ></i>
                <Tooltip
                  target="#other-name-info"
                  content="Any middle or extra names that the employee has. You can leave this blank if it doesn’t apply."
                  position="right"
                  className="custom-tooltip tooltip-custom-format"
                />
              </label>
              {states?.loadingData ? (
                <Skeleton
                  data-testid="skeleton-loader"
                  className="p-4"
                ></Skeleton>
              ) : (
                <InputText
                  value={states?.workosuser?.other_name}
                  placeholder="Other Name"
                  name="other_name"
                  type="text"
                  disabled
                  className="appearance-none outline-none w-full disabled-input"
                />
              )}
            </div>

            <div className="field col-12 md:col-12 mb-0 mt-3">
              <label className="input-label">
                Surname(s)
                <i
                  className="ml-2 pi pi-info-circle info-icon"
                  id="sur-name-info"
                ></i>
                <Tooltip
                  target="#sur-name-info"
                  content="This is usually a family name or last name. If employee has more than one, include them here."
                  position="right"
                  className="custom-tooltip tooltip-custom-format"
                />
              </label>
              {states?.loadingData ? (
                <Skeleton
                  data-testid="skeleton-loader"
                  className="p-4"
                ></Skeleton>
              ) : (
                <InputText
                  value={states?.workosuser?.last_name}
                  placeholder="Enter Surname(s)*"
                  name="lastName"
                  type="text"
                  disabled
                  className="appearance-none outline-none w-full disabled-input"
                />
              )}
            </div>

            <div className="field col-12 md:col-12 mb-0 mt-3">
              <label className="input-label">
                Preferred name(s)
                <i
                  className="ml-2 pi pi-info-circle info-icon"
                  id="preffered-name-info"
                ></i>
                <Tooltip
                  target="#preffered-name-info"
                  content="If the employee wants to be called by a different name than their given name, put that here."
                  position="right"
                  className="custom-tooltip tooltip-custom-format"
                />
              </label>
              {states?.loadingData ? (
                <Skeleton
                  data-testid="skeleton-loader"
                  className="p-4"
                ></Skeleton>
              ) : (
                <InputText
                  value={states?.workosuser?.preferred_name}
                  placeholder="Preferred Name or Nickname"
                  name="preferred_name"
                  type="text"
                  disabled
                  className="appearance-none outline-none w-full disabled-input"
                />
              )}
            </div>

            <div className="field lg:col-6 md:col-6 sm:col-12 xs:col-12 mb-0 mt-3">
              <label className="input-label">Email</label>
              {states?.loadingData ? (
                <Skeleton
                  data-testid="skeleton-loader"
                  className="p-4"
                ></Skeleton>
              ) : (
                <InputText
                  value={states?.workosuser?.email}
                  placeholder="evaccaro@company.com"
                  name="email"
                  type="text"
                  disabled
                  className="appearance-none outline-none w-full disabled-input"
                />
              )}
            </div>
            <div className="field lg:col-6 md:col-6 sm:col-12 xs:col-12 mb-0 mt-3">
              <label className="input-label">Organization</label>
              {states?.loadingData ? (
                <Skeleton
                  data-testid="skeleton-loader"
                  className="p-4"
                ></Skeleton>
              ) : (
                <div className="icon-field">
                  <InputText
                    value={states?.workosuser?.organization}
                    placeholder="Organisation"
                    type="text"
                    name="organisation"
                    disabled
                    className="appearance-none outline-none w-full disabled-input"
                  />
                  <i className="pi pi-lock input-icon"></i>
                </div>
              )}
            </div>
            <div className="field lg:col-6 md:col-6 sm:col-12 xs:col-12 mb-0 mt-2">
              <label className="input-label">User role</label>
              {states?.loadingData ? (
                <Skeleton
                  data-testid="skeleton-loader"
                  className="p-4"
                ></Skeleton>
              ) : (
                <Dropdown
                  options={[
                    { label: "GXM Admin User", value: "GXM_ADMIN" },
                    { label: "GXM User", value: "GXM_USER" },
                    { label: "GXM Manager", value: "GXM_MANAGER" },
                    { label: "LSP User", value: "LSP_USER" },
                    { label: "LSP Manager", value: "LSP_MANAGER" },
                    { label: "Customer Manager", value: "CUSTOMER_MANAGER" },
                    { label: "Customer User", value: "CUSTOMER" },
                  ]}
                  loading={!role}
                  value={role}
                  className="w-full"
                  placeholder={!role ? "Loading..." : "Role"}
                  onChange={(e) => handleRoleChange(e)}
                />
              )}
            </div>

            {["LSP_USER", "LSP_MANAGER"]?.includes(
              states?.workosuser?.role,
            ) && (
              <div className="field lg:col-6 md:col-6 sm:col-12 xs:col-12 mb-0 mt-2">
                <div className="mb-2">
                  <label className="input-label ">Local Service Provider</label>
                </div>

                {states?.entitylistLoading ? (
                  <Skeleton
                    data-testid="skeleton-loader"
                    className="p-4"
                  ></Skeleton>
                ) : (
                  <Dropdown
                    value={states?.userlsp || {}}
                    onChange={(e) => {
                      if (!isAdmin) {
                        return null;
                      }
                      handleAssignLsp(e);
                    }}
                    loading={states?.entitylistLoading}
                    options={states?.lspList}
                    optionLabel="name"
                    optionValue="id"
                    placeholder="Local Service Provider"
                    className="w-full"
                    // disabled
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </Card>
    </>
  );
};
export default UserInfo;
